import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchProducts, getProductCategories } from '../services/dataService';
import './Products.css';

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const categories = getProductCategories();

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const data = await fetchProducts();
        setProducts(data);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  // Filter products based on category and search term
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="products-loading">
        <div className="container">
          <div className="loading-content">
            <div className="loading"></div>
            <p>Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products">
      {/* Hero Section */}
      <section className="products-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-layout">
              <div className="hero-left">
                <h1 className="hero-title">
                  <span className="hero-title-line1">Our</span>
                  <span className="hero-title-line2">Products</span>
                </h1>
              </div>
              <div className="hero-center">
                <p className="hero-subtitle">Quality fertilizers and gardening supplies</p>
              </div>
              <div className="hero-right">
                <p className="hero-description">
                  Discover our wide range of fertilizers, tools, plants, garden dercoratives & all other gardening needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="filters-section">
        <div className="container">
          <div className="filters-content">
            {/* Search Bar */}
            <div className="search-container">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Category Filters */}
            <div className="category-filters">
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="products-section">
        <div className="container">
          <div className="products-header">
            <h2 className="section-title">
              {selectedCategory === 'all' ? 'All Products' : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="products-count">
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {filteredProducts.length > 0 ? (
            <div className={`products-grid ${filteredProducts.length <= 2 ? 'few-items' : ''}`}>
              {filteredProducts.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    <img src={product.image} alt={product.name} />
                    <div className="product-category-badge">
                      {categories.find(c => c.id === product.category)?.name}
                    </div>
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <div className="product-actions">
                      <Link to={`/products/${product.slug || product.id}`} className="btn btn-primary">
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-products">
              <div className="no-products-content">
                <h3>No products found</h3>
                <p>Try adjusting your search or filter criteria.</p>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="products-cta">
        <div className="container">
          <div className="banner-content">
            <div className="banner-text">
              <h2 className="banner-title">Need Help Choosing?</h2>
              <p className="banner-description">
                Our gardening experts are here to help you select the right products for your needs.
              </p>
            </div>
            <div className="banner-actions">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <Link to="/blog" className="btn btn-primary">
                Read Our Blog
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;